import React from 'react';
import { useIntl } from 'react-intl';
import useNavigation from 'enhancers/use-navigation';
import useReadonlyObservable from 'enhancers/use-readonly-observable';
import appLanguage from 'services/i18n/language';
import location from 'services/router/location';
import application from 'services/application';
import Button from 'components/button';
import type { PublicSelectElement } from 'components/select';
import Select from 'components/select';
import Option from 'components/select/option';

import loginMessages from '../../login/login.i18n';
import signupMessages from '../../signup/registration/registration.i18n';

import messages from './app-header-controls.i18n';
import { testId, langCodes } from './app-header-controls.settings';
import styles from './app-header-controls.module.scss';

interface AppHeaderControlsProps {
  login?: boolean;
  signup?: boolean;
  showLanguageSelector?: boolean;
}

const AppHeaderControls: React.FunctionComponent<AppHeaderControlsProps> = (props) => {
  const { login, signup, showLanguageSelector = false } = props;
  const { formatMessage } = useIntl();
  const [navigation] = useNavigation();
  const [currentLanguage] = useReadonlyObservable(appLanguage.onChange$, appLanguage.current);

  const handleOnLangChange: PublicSelectElement['onChange'] = React.useCallback(
    (value): void => {
      navigation[location.current.name](
        { ...location.params(), lang: value, ...location.searchParams(true) },
        'replace'
      );
    },
    [navigation]
  );

  const renderDefaultLanguageSelector = React.useCallback(() => {
    return (
      <Select
        onChange={handleOnLangChange}
        className={styles.languageDropdown}
        label={currentLanguage.toUpperCase()}
        testId={testId.languageSelector}
        isMinimal
      >
        {langCodes.map((langCode) => {
          const languageKey = langCode as keyof typeof messages.pages.shared.header.languages;

          return (
            <Option key={langCode} value={langCode} selected={langCode === currentLanguage}>
              {formatMessage(messages.pages.shared.header.languages[languageKey], {
                langCode: langCode.toUpperCase(),
              })}
            </Option>
          );
        })}
      </Select>
    );
  }, [currentLanguage, formatMessage, handleOnLangChange]);

  React.useEffect(() => {
    application.header.setContent(
      <div className="d-inline-flex align-items-center gap-1">
        {showLanguageSelector && renderDefaultLanguageSelector()}
        <div className="ms-2 text-nowrap">
          {signup && (
            <Button onClick={() => navigation.signup()} size="xs" className={login ? 'me-2' : undefined}>
              {formatMessage(loginMessages.pages.login.signup[application.current].cta)}
            </Button>
          )}
          {login && (
            <Button onClick={() => navigation.login()} variant="secondary1" size="xs">
              {formatMessage(signupMessages.pages.signup.registration.login[application.current].cta)}
            </Button>
          )}
        </div>
      </div>
    );

    return () => {
      application.header.clearContent();
    };
  }, [formatMessage, login, signup, showLanguageSelector, renderDefaultLanguageSelector, navigation]);

  return null;
};

export type { AppHeaderControlsProps };
export default AppHeaderControls;
